<script setup>
// NOTICE: KAPTION-INTEGRATION
// ai-connectors.PAGE: Settings → AI connectors (replaces the Rumo MCP page
// when KAPTION_AI_CONNECTORS is on).
//   PAGE.1  How to connect: Claude, ChatGPT and Notion first, the rest under
//           "More AI tools". Each shows its own steps and the connector URL.
//   MINE.1  Your own connections, across accounts; anyone can disconnect theirs.
//   PAGE.2  Admins also see everyone's connections into this account.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import kaptionMcpGrant from 'dashboard/api/kaptionMcpGrant';
import kaptionAiConnections from 'dashboard/api/kaptionAiConnections';
import kaptionAccessTokens from 'dashboard/api/kaptionAccessTokens';
import KaptionAiClientLogo from './KaptionAiClientLogo.vue';
import {
  AI_CLIENTS,
  matchAiClient,
  installLink,
  SETTINGS_LINKS,
} from './kaptionAiClients';

const { t } = useI18n();
const getters = useStoreGetters();

const isAdmin = computed(
  () => getters.getCurrentRole.value === 'administrator'
);

const loading = ref(true);
const mcpUrl = ref('https://mcp.rumohq.com/mcp');
const openClient = ref('claude');
const showMore = ref(false);
const mine = ref([]);
const accountConnections = ref([]);
const confirmingId = ref(null);

const featured = AI_CLIENTS.filter(c => !c.more);
const more = AI_CLIENTS.filter(c => c.more);

const loadMine = async () => {
  try {
    const { data } = await kaptionAiConnections.get();
    mine.value = data.payload || [];
  } catch (e) {
    useAlert(t('KAPTION_AI_CONNECTORS.ERROR'));
  }
};

const loadAccount = async () => {
  if (!isAdmin.value) return;
  try {
    const { data } = await kaptionAccessTokens.getAll({ purpose: 'mcp' });
    accountConnections.value = (data.payload || data).filter(
      c => c.status === 'active'
    );
  } catch (e) {
    // the admin list is optional on this page
  }
};

onMounted(async () => {
  try {
    const { data } = await kaptionMcpGrant.getStatus();
    if (data.mcp_url) mcpUrl.value = data.mcp_url;
  } catch (e) {
    // keep the default URL
  }
  await Promise.all([loadMine(), loadAccount()]);
  loading.value = false;
});

const copyUrl = async () => {
  await copyTextToClipboard(mcpUrl.value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const toggleClient = id => {
  openClient.value = openClient.value === id ? null : id;
};

const oneClick = id => installLink(id, mcpUrl.value);

const disconnectMine = async connection => {
  if (confirmingId.value !== `mine-${connection.id}`) {
    confirmingId.value = `mine-${connection.id}`;
    return;
  }
  try {
    await kaptionAiConnections.disconnect(connection.id);
    useAlert(t('KAPTION_AI_CONNECTORS.DISCONNECTED'));
    confirmingId.value = null;
    await Promise.all([loadMine(), loadAccount()]);
  } catch (e) {
    useAlert(t('KAPTION_AI_CONNECTORS.ERROR'));
  }
};

const disconnectAny = async connection => {
  if (confirmingId.value !== `acct-${connection.id}`) {
    confirmingId.value = `acct-${connection.id}`;
    return;
  }
  try {
    await kaptionAccessTokens.revoke(connection.id);
    useAlert(t('KAPTION_AI_CONNECTORS.DISCONNECTED'));
    confirmingId.value = null;
    await Promise.all([loadMine(), loadAccount()]);
  } catch (e) {
    useAlert(t('KAPTION_AI_CONNECTORS.ERROR'));
  }
};

const formatDate = value => (value ? new Date(value).toLocaleDateString() : '');

const roleNames = roles =>
  (roles || [])
    .map(key => {
      const path = `KAPTION_AI_CONNECTORS.ROLES.${key.toUpperCase()}.NAME`;
      const label = t(path);
      return label === path ? key : label;
    })
    .join(', ');

const accountsLabel = connection =>
  connection.all_accounts
    ? t('KAPTION_AI_CONNECTORS.ALL_ACCOUNTS')
    : (connection.accounts || []).map(a => a.name || `#${a.id}`).join(', ');

const usedLabel = connection =>
  connection.last_used_at
    ? t('KAPTION_AI_CONNECTORS.LAST_USED', {
        date: formatDate(connection.last_used_at),
      })
    : t('KAPTION_AI_CONNECTORS.NEVER_USED');

// Tokens are named "<client> (MCP) — <person>".
const clientOf = name => String(name || '').split(' (MCP)')[0];
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="$t('KAPTION_MCP_INTEGRATION.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_AI_CONNECTORS.NAME')"
        :description="$t('KAPTION_AI_CONNECTORS.DESCRIPTION')"
        feature-name="rumo_mcp"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-5" data-testid="ai-connectors-page">
        <!-- PAGE.1: how to connect -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1">
          <div
            v-for="item in [...featured, ...(showMore ? more : [])]"
            :key="item.id"
            class="border-b border-n-weak last:border-b-0"
          >
            <button
              type="button"
              class="flex items-center w-full gap-3 px-5 py-4 text-left hover:bg-n-alpha-black2"
              :data-testid="`ai-client-${item.id}`"
              :aria-expanded="openClient === item.id"
              @click="toggleClient(item.id)"
            >
              <KaptionAiClientLogo :client-id="item.id" />
              <span class="flex-1 text-sm font-medium text-n-slate-12">
                {{
                  $t('KAPTION_AI_CONNECTORS.CONNECT_TO', { client: item.name })
                }}
              </span>
              <span
                v-if="oneClick(item.id)"
                class="px-1.5 py-0.5 text-[11px] rounded bg-n-teal-3 text-n-teal-11"
              >
                {{ $t('KAPTION_AI_CONNECTORS.ONE_CLICK') }}
              </span>
              <Icon
                :icon="
                  openClient === item.id
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
                class="size-4 text-n-slate-11"
              />
            </button>
            <div
              v-if="openClient === item.id"
              class="px-5 pb-5 ltr:pl-[4.25rem] rtl:pr-[4.25rem]"
              :data-testid="`ai-client-steps-${item.id}`"
            >
              <p class="text-sm text-n-slate-11">
                {{ $t(`KAPTION_AI_CONNECTORS.STEPS.${item.id.toUpperCase()}`) }}
              </p>
              <div
                v-if="!oneClick(item.id)"
                class="flex items-center gap-2 mt-3"
              >
                <code
                  class="flex items-center flex-1 h-10 px-3 overflow-x-auto text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 whitespace-nowrap"
                  data-testid="ai-connector-url"
                >
                  {{ mcpUrl }}
                </code>
                <Button
                  type="button"
                  faded
                  icon="i-lucide-copy"
                  :label="$t('KAPTION_AI_CONNECTORS.COPY')"
                  class="flex-shrink-0"
                  @click="copyUrl"
                />
              </div>
              <div class="flex flex-wrap items-center gap-2 mt-3">
                <a
                  v-if="oneClick(item.id)"
                  :href="oneClick(item.id)"
                  :data-testid="`ai-install-${item.id}`"
                >
                  <Button
                    type="button"
                    icon="i-lucide-download"
                    :label="
                      $t('KAPTION_AI_CONNECTORS.ADD_TO', { client: item.name })
                    "
                  />
                </a>
                <a
                  v-else-if="SETTINGS_LINKS[item.id]"
                  :href="SETTINGS_LINKS[item.id]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    type="button"
                    faded
                    icon="i-lucide-external-link"
                    :label="
                      $t('KAPTION_AI_CONNECTORS.OPEN_CLIENT', {
                        client: item.name,
                      })
                    "
                  />
                </a>
                <span v-if="!oneClick(item.id)" class="text-xs text-n-slate-10">
                  {{
                    $t('KAPTION_AI_CONNECTORS.THEN_NOTE', { client: item.name })
                  }}
                </span>
              </div>
            </div>
          </div>
          <!-- CLIENTS.2: any app that speaks MCP -->
          <div class="border-b border-n-weak last:border-b-0">
            <button
              type="button"
              class="flex items-center w-full gap-3 px-5 py-4 text-left hover:bg-n-alpha-black2"
              data-testid="ai-client-any"
              :aria-expanded="openClient === 'any'"
              @click="toggleClient('any')"
            >
              <span
                class="flex items-center justify-center flex-shrink-0 border rounded-lg size-9 border-n-weak bg-n-solid-1"
              >
                <Icon icon="i-lucide-plug" class="size-4 text-n-slate-11" />
              </span>
              <span class="flex flex-col flex-1 min-w-0">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('KAPTION_AI_CONNECTORS.ANY_MCP.NAME') }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ $t('KAPTION_AI_CONNECTORS.ANY_MCP.DESC') }}
                </span>
              </span>
              <Icon
                :icon="
                  openClient === 'any'
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
                class="size-4 text-n-slate-11"
              />
            </button>
            <div
              v-if="openClient === 'any'"
              class="px-5 pb-5 ltr:pl-[4.25rem] rtl:pr-[4.25rem]"
              data-testid="ai-client-steps-any"
            >
              <p class="text-sm text-n-slate-11">
                {{ $t('KAPTION_AI_CONNECTORS.ANY_MCP.STEP') }}
              </p>
              <div class="flex items-center gap-2 mt-3">
                <code
                  class="flex items-center flex-1 h-10 px-3 overflow-x-auto text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 whitespace-nowrap"
                >
                  {{ mcpUrl }}
                </code>
                <Button
                  type="button"
                  faded
                  icon="i-lucide-copy"
                  :label="$t('KAPTION_AI_CONNECTORS.COPY')"
                  class="flex-shrink-0"
                  @click="copyUrl"
                />
              </div>
            </div>
          </div>
          <button
            v-if="!showMore"
            type="button"
            class="flex items-center gap-2 w-full px-5 py-3 text-sm text-n-slate-11 hover:bg-n-alpha-black2 rounded-b-xl"
            data-testid="ai-clients-more"
            @click="showMore = true"
          >
            <Icon icon="i-lucide-plus" class="size-4" />
            {{ $t('KAPTION_AI_CONNECTORS.MORE') }}
            <span class="flex gap-1 ltr:ml-1 rtl:mr-1">
              <KaptionAiClientLogo
                v-for="item in more"
                :key="item.id"
                :client-id="item.id"
                size="sm"
              />
            </span>
          </button>
        </div>

        <!-- MINE.1: your connections -->
        <div class="rounded-xl border border-n-weak bg-n-solid-1">
          <h5 class="px-5 py-4 text-sm font-semibold text-n-slate-12">
            {{ $t('KAPTION_AI_CONNECTORS.MINE_TITLE') }}
          </h5>
          <p
            v-if="!mine.length"
            class="px-5 pb-5 text-sm text-n-slate-11"
            data-testid="ai-mine-empty"
          >
            {{ $t('KAPTION_AI_CONNECTORS.MINE_EMPTY') }}
          </p>
          <div v-else class="border-t border-n-weak divide-y divide-n-weak">
            <div
              v-for="connection in mine"
              :key="connection.id"
              class="flex items-center gap-4 px-5 py-3"
              :data-testid="`ai-mine-${connection.id}`"
            >
              <KaptionAiClientLogo
                :client-id="matchAiClient(connection.client)?.id || ''"
                size="sm"
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium truncate text-n-slate-12">
                  {{ connection.client }}
                  <span class="font-normal text-n-slate-11">
                    · {{ roleNames(connection.roles) }}
                  </span>
                </div>
                <p class="text-xs truncate text-n-slate-10">
                  {{ accountsLabel(connection) }} ·
                  {{
                    $t('KAPTION_AI_CONNECTORS.CONNECTED_ON', {
                      date: formatDate(connection.created_at),
                    })
                  }}
                  · {{ usedLabel(connection) }}
                </p>
              </div>
              <Button
                type="button"
                faded
                ruby
                sm
                :label="
                  confirmingId === `mine-${connection.id}`
                    ? $t('KAPTION_AI_CONNECTORS.DISCONNECT_CONFIRM')
                    : $t('KAPTION_AI_CONNECTORS.DISCONNECT')
                "
                class="flex-shrink-0"
                @click="disconnectMine(connection)"
              />
            </div>
          </div>
        </div>

        <!-- PAGE.2: admins see everyone's connections in this account -->
        <div
          v-if="isAdmin"
          class="rounded-xl border border-n-weak bg-n-solid-1"
          data-testid="ai-account-connections"
        >
          <div class="px-5 py-4">
            <h5 class="text-sm font-semibold text-n-slate-12">
              {{ $t('KAPTION_AI_CONNECTORS.ACCOUNT_TITLE') }}
            </h5>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('KAPTION_AI_CONNECTORS.ACCOUNT_DESC') }}
            </p>
          </div>
          <p
            v-if="!accountConnections.length"
            class="px-5 pb-5 text-sm text-n-slate-11"
          >
            {{ $t('KAPTION_MCP_INTEGRATION.EMPTY') }}
          </p>
          <div v-else class="border-t border-n-weak divide-y divide-n-weak">
            <div
              v-for="connection in accountConnections"
              :key="connection.id"
              class="flex items-center gap-4 px-5 py-3"
            >
              <KaptionAiClientLogo
                :client-id="matchAiClient(clientOf(connection.name))?.id || ''"
                size="sm"
              />
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium truncate text-n-slate-12">
                  {{ clientOf(connection.name) }}
                  <span class="font-normal text-n-slate-11">
                    · {{ connection.owner?.name }}
                  </span>
                </div>
                <p class="text-xs truncate text-n-slate-10">
                  {{ roleNames(connection.roles) }} ·
                  {{ usedLabel(connection) }}
                </p>
              </div>
              <Button
                type="button"
                faded
                ruby
                sm
                :label="
                  confirmingId === `acct-${connection.id}`
                    ? $t('KAPTION_AI_CONNECTORS.DISCONNECT_CONFIRM')
                    : $t('KAPTION_AI_CONNECTORS.DISCONNECT')
                "
                class="flex-shrink-0"
                @click="disconnectAny(connection)"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
